import { v4 } from 'uuid';

import { iaRa } from 'lib/helpers/utility';

import NotFound from 'components/practice/Dashboard/components/NotFound';
import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import SegmentedControl from 'components/shared/SegmentedControl/SegmentedControl';
import Skeleton from 'components/shared/Skeleton/Skeleton';

import { toggleSwitchItems } from './constants';

export const handleFormatTodaysCpt = (cpt) => {
  return {
    uuid: v4(),
    procedure_code: cpt.code,
    units: 1,
    name: getProcedureName(cpt, 'code'),
    ndc_code: '',
    modifiers: [],
    icd_10_codes: [],
    note: ''
  };
};

export const getProcedureName = (cpt = {}, codeField = 'procedure_code') => {
  const codeValue = cpt[codeField] || '';

  let procedureName = codeValue;

  if (cpt.name) {
    if (codeValue) {
      procedureName = cpt.name.includes(codeValue) ? cpt.name : `${codeValue} - ${cpt.name}`;
    } else {
      procedureName = cpt.name;
    }
  }

  return procedureName;
};

// content to show when no items in transfer section
export const onEmptyTodayCodes = (section, activeTab) => {
  if (section === 'allCodesSection' && activeTab === 'past') {
    return (
      <NotFound
        title="No past CPT® codes"
        description="This patient doesn't have any CPT® Codes from previous visits. To add a diagnosis, switch to the All tab and choose from the full list of codes."
        icon="illustration-empty-state-default"
        className="h-full p-[54px]"
      />
    );
  }

  if (section === 'todayCodesSection') {
    return (
      <NotFound
        title="No codes added yet"
        description="Add CPT® Codes from the left panel."
        icon="illustration-no-content"
        className="h-full p-[54px]"
      />
    );
  }
};

// content to show in header of section
export const handleShowHeader = ({ section, activeTab, setActiveTab }) => {
  return (
    section === 'allCodesSection' && (
      <SegmentedControl
        active={activeTab}
        items={toggleSwitchItems}
        className="w-auto"
        onClick={(item) => setActiveTab(item.value)}
      />
    )
  );
};

// render any content below the header
export const columnToolbar = ({
  section,
  activeTab,
  showDeleteSection,
  debouncedSetSearchTerm
}) => {
  if (section === 'allCodesSection') {
    // render custom section content for example delete red section when moving items from todayCodesSection
    if (showDeleteSection) {
      return (
        <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center rounded-md border-[1px] border-dashed border-danger-500 !bg-danger-50">
          <div className="w-fit rounded-full bg-danger-100 p-3 text-white">
            <Icon icon="trash-mono" color="danger" size={40} />
          </div>
        </div>
      );
    }

    if (activeTab === 'past') {
      return (
        <div className="h-8 place-content-center px-3 text-sm text-neutral-800">
          CPT® Codes previously used for this patient
        </div>
      );
    }

    return (
      <Input
        icon="search-linear"
        placeholder="Type and search codes"
        className="px-3"
        id="search-cpt-codes"
        onChange={debouncedSetSearchTerm}
      />
    );
  }
};

// loading skeleton when fetching data
export const renderLoading = ({ section, isLoading }) => {
  if (section === 'allCodesSection' && isLoading) {
    return (
      <div className="flex-1 overflow-y-auto px-3">
        <Skeleton height={52} count={15} />
      </div>
    );
  }
};

export const getPastCptFromPastClinical = ({
  clinical,
  patientClinicalNotes,
  formatDate = () => ''
}) => {
  const pastClinicalNotes = iaRa(patientClinicalNotes).filter(
    (el) => el?.appointment_id !== clinical?.appointment_id
  );

  const groupedData = pastClinicalNotes.reduce((acc, item) => {
    const date = formatDate(item?.appointment?.starts_at);

    if (!acc[date]) acc[date] = [];

    const newCodes = item?.cpt_codes?.filter((cpt) => cpt?.procedure_code) || [];

    acc[date].push(...newCodes);

    return acc;
  }, {});

  const pastCpt = Object.entries(groupedData)
    .map(([created_at, procedures]) => {
      if (procedures.length) {
        return { created_at, procedures };
      }
    })
    .filter(Boolean);

  const formattedPastCpt = pastCpt.flatMap(({ created_at, procedures }) => {
    const result = [];

    result.push({ groupDate: created_at, code: created_at });

    result.push(
      ...procedures.map((cpt) => ({
        name: cpt.name,
        code: cpt.procedure_code // used code to display in AllCodesItem
      }))
    );

    return result;
  });

  return formattedPastCpt;
};
